import { useMemo } from 'react';
import { useToolEvents } from '../lib/tool-stream';
const LCAT_STEPS = [
    {
        step: 'fetch-attachments',
        label: 'Fetch Attachments'
    },
    {
        step: 'detect-schedule-b',
        label: 'Detect Schedule B'
    },
    {
        step: 'identify-pws',
        label: 'Identify PWS'
    },
    {
        step: 'parse-pws',
        label: 'Parse PWS'
    },
    {
        step: 'analyze-clins',
        label: 'Analyze CLINs'
    },
    {
        step: 'extract-roles',
        label: 'Extract Functional Roles'
    },
    {
        step: 'map-attachments',
        label: 'Map Attachments'
    },
    {
        step: 'compliance-matrix',
        label: 'Generate Compliance Matrix'
    },
    {
        step: 'generate-outputs',
        label: 'Generate Outputs'
    }
];
/**
 * Hook to aggregate LCAT progress events by step
 * Filters tool events for data-lcat-progress type and organizes by step
 *
 * @param _messageId - Reserved for future use (message-level filtering)
 * @param lcatRunId - Optional LCAT run ID to filter events for a specific run
 */ export function useLCATProgress(_messageId, lcatRunId) {
    const { events } = useToolEvents((ev)=>{
        if (ev.type !== 'data-lcat-progress') {
            return false;
        }
        // If lcatRunId is provided, only include events with matching ID
        if (lcatRunId && ev.id !== lcatRunId) {
            return false;
        }
        return true;
    });
    const steps = useMemo(()=>{
        // Create a map of step name to latest status
        const stepMap = new Map();
        // Initialize all steps as pending
        LCAT_STEPS.forEach(({ step, label })=>{
            stepMap.set(step, {
                step,
                status: 'pending',
                label
            });
        });
        // Track the highest priority status and completion timestamps for each step
        // This ensures we preserve completion state even if events are lost
        const stepBestState = new Map();
        // Status priority: completed > error > in_progress > started > pending
        const statusPriority = {
            completed: 5,
            error: 4,
            in_progress: 3,
            started: 2,
            pending: 1
        };
        // Update with actual events - process in order to maintain state progression
        events.forEach((event)=>{
            const step = event.step;
            const status = event.status;
            const details = event.details;
            const progress = event.progress;
            const timestamp = event.timestamp;
            // Handle error step separately
            if (step === 'error' || step === 'processing') {
                // Mark all non-completed steps as error
                stepMap.forEach((stepProgress, key)=>{
                    if (stepProgress.status !== 'completed') {
                        stepMap.set(key, {
                            ...stepProgress,
                            status: 'error',
                            details: details || 'Processing failed',
                            timestamp
                        });
                    }
                });
                return;
            }
            const stepInfo = LCAT_STEPS.find((s)=>s.step === step);
            if (stepInfo) {
                const currentBest = stepBestState.get(step);
                const newPriority = statusPriority[status] || 0;
                // Only update if this status has higher priority than what we've seen
                if (!currentBest || newPriority > currentBest.priority) {
                    stepBestState.set(step, {
                        status,
                        priority: newPriority,
                        timestamp: status === 'completed' ? timestamp : currentBest?.timestamp
                    });
                    stepMap.set(step, {
                        step,
                        status,
                        details,
                        progress,
                        timestamp,
                        label: stepInfo.label
                    });
                } else if (currentBest.priority === newPriority) {
                    // Same priority - update details/progress but keep status
                    stepMap.set(step, {
                        step,
                        status: currentBest.status,
                        details,
                        progress,
                        timestamp: currentBest.timestamp || timestamp,
                        label: stepInfo.label
                    });
                }
            // Lower priority status is ignored - preserves completed/error states
            }
        });
        return Array.from(stepMap.values());
    }, [
        events
    ]);
    // Extract and cache completion/error messages (memoized to preserve even if events are lost)
    const { completionMessage, errorMessage } = useMemo(()=>{
        const completionEvent = events.find((e)=>e.step === 'complete');
        const errorEvent = events.find((e)=>e.step === 'error');
        return {
            completionMessage: completionEvent?.details,
            errorMessage: errorEvent?.details
        };
    }, [
        events
    ]);
    const hasStarted = events.length > 0;
    const hasError = steps.some((s)=>s.status === 'error') || !!errorMessage;
    const isComplete = steps.every((s)=>s.status === 'completed' || s.status === 'error' || s.status === 'pending') && steps.some((s)=>s.status === 'completed') || !!completionMessage;
    const currentStep = steps.find((s)=>s.status === 'in_progress' || s.status === 'started');
    return {
        steps,
        hasStarted,
        hasError,
        isComplete,
        currentStep,
        completionMessage,
        errorMessage
    };
}
